/* RESPONSIBLE TEAM: team-standalone */

import Platform from './platform';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { request } from 'embercom/lib/inbox/requests';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import type { ConnectionType } from 'embercom/objects/environments/connection-types';

/**
 * Abstract base class for standardized platforms (HubSpot, Freshdesk) that use
 * the newer standardized Integration endpoints.
 *
 * Supports multi-env configuration tracking via generic type parameter.
 */
export default abstract class StandardizedPlatform<TConfig = any> extends Platform {
  @service declare appService: AppService;
  @service declare store: Store;

  @tracked private loadingDataFields = false;

  // Multi-configuration support
  @tracked configurations?: Array<TConfig>;
  @tracked activeConfiguration?: TConfig;

  switchToConfiguration(config: TConfig) {
    this.activeConfiguration = config;
  }

  protected abstract get connectionTypes(): ConnectionType[];

  async loadRequiredData() {
    await this.loadDataFields();
  }

  async loadDataFields() {
    this.loadingDataFields = true;
    let appId = this.appService.app.id;

    let connections = await this.store.findAll('standalone/connection');

    // Build all fetch promises for maximum parallelism
    let allFetchPromises: Array<Promise<any[]>> = [];

    for (let connectionType of this.connectionTypes) {
      let matchingConnections = connections.filter(
        (connection) => connection.connectionType === connectionType,
      );

      for (let connection of matchingConnections) {
        let baseUrl = `/ember/standalone/connections/${connection.connectionType}/${connection.id}`;

        // Create parallel fetch promises for all three field types
        allFetchPromises.push(
          this.fetchFields(`${baseUrl}/available_company_fields?app_id=${appId}`),
          this.fetchFields(`${baseUrl}/available_conversation_fields?app_id=${appId}`),
          this.fetchFields(`${baseUrl}/available_user_fields?app_id=${appId}`),
        );
      }
    }

    let results = await Promise.all(allFetchPromises);

    // Set composite id first so uniqueById works across field types
    let allFields = results.flat().map((f) => ({
      ...f,
      id: `${f.intercom_field_type}/${f.external_id}`,
    }));

    this.store.pushPayload('standalone/external-field-identifier', {
      'standalone/external-field-identifier': this.uniqueById(allFields),
    });

    this.loadingDataFields = false;
  }

  private async fetchFields(url: string): Promise<any[]> {
    try {
      let response = await request(url);
      let data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (e: unknown) {
      console.error(`Failed to load ${this.platformKey} fields from ${url}:`, e);
      return [];
    }
  }

  get isLoadingDataFields() {
    return this.loadingDataFields;
  }

  private uniqueById(items: any[]): any[] {
    let seen = new Set<string>();
    return items.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }

  isMappedUserAttribute(attributeIdentifier: string) {
    return this.userFields.any(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  isMappedCompanyAttribute(attributeIdentifier: string) {
    return this.companyFields.any(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  isMappedConversationAttribute(attributeIdentifier: string) {
    return this.ticketFields.any(
      (externalFieldIdentifier) =>
        externalFieldIdentifier.intercomAttributeIdentifier === attributeIdentifier,
    );
  }

  get ticketFields() {
    return this.store
      .peekAll('standalone/external-field-identifier')
      .filter((field) => field.isConversationField);
  }

  get userFields() {
    return this.store
      .peekAll('standalone/external-field-identifier')
      .filter((field) => field.isUserField);
  }

  get companyFields() {
    return this.store
      .peekAll('standalone/external-field-identifier')
      .filter((field) => field.isCompanyField);
  }
}
